import React from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getSchema } from '../api/schemas'
import { getProject } from '../api/projects'
import { getSchemaLayout } from '../api/schemas'
import type { SchemaLayoutItem, Schema, Project } from '../types'

interface SpecificationItem {
  specification_id: number
  spec_name: string
  device_type: string
  count: number
  positions: string[]
}

export default function Specification() {
  const { projectId, schemaId } = useParams<{ projectId: string; schemaId: string }>()

  const { data: project } = useQuery<Project>({
    queryKey: ['project', projectId],
    queryFn: () => getProject(Number(projectId)),
    enabled: !!projectId,
  })

  const { data: schema, isLoading } = useQuery<Schema>({
    queryKey: ['schema', schemaId],
    queryFn: () => getSchema(Number(schemaId)),
    enabled: !!schemaId,
  })

  const { data: schemaLayoutResponse } = useQuery({
    queryKey: ['schemaLayout', schemaId],
    queryFn: () => getSchemaLayout(Number(schemaId)),
    enabled: !!schemaId,
  })

  const schemaLayout = schemaLayoutResponse?.data || []

  // Group devices by specification_id and count them
  const specificationItems: SpecificationItem[] = React.useMemo(() => {
    const groups: Record<number, SpecificationItem> = {}

    schemaLayout.forEach((item: SchemaLayoutItem) => {
      const specId = item.specification_id
      
      if (!groups[specId]) {
        groups[specId] = {
          specification_id: specId,
          spec_name: item.spec_name || 'Unknown Device',
          device_type: item.device_type || 'device',
          count: 0,
          positions: []
        }
      }
      
      groups[specId].count++
      if (item.position_label) {
        groups[specId].positions.push(item.position_label)
      }
    })

    return Object.values(groups).sort((a, b) => 
      a.device_type.localeCompare(b.device_type) || a.spec_name.localeCompare(b.spec_name)
    )
  }, [schemaLayout])

  // Group by device type for better organization
  const groupedByType = React.useMemo(() => {
    const typeGroups: Record<string, SpecificationItem[]> = {}
    
    specificationItems.forEach(item => {
      const type = item.device_type || 'Прочее'
      if (!typeGroups[type]) {
        typeGroups[type] = []
      }
      typeGroups[type].push(item)
    })

    return typeGroups
  }, [specificationItems])

  const getDeviceTypeDisplayName = (deviceType: string): string => {
    const typeNames: Record<string, string> = {
      'cable': 'Кабели',
      'light': 'Светильники',
      'socket': 'Розетки',
      'terminal': 'Клеммники',
      'breaker': 'Автоматы',
      'relay': 'Реле',
      'actuator': 'Актуаторы',
      'knx_module': 'KNX модули',
      'device': 'Устройства',
      'protection': 'Устройства защиты'
    }
    return typeNames[deviceType] || deviceType
  }

  if (isLoading) {
    return <div className="text-center py-8">Loading specification...</div>
  }

  if (!schema) {
    return <div className="text-center py-8 text-red-600">Schema not found</div>
  }

  const totalItems = specificationItems.reduce((sum, item) => sum + item.count, 0)

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Link 
              to={`/projects/${projectId}/schemas/${schemaId}`}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              ← Back to Schema Editor
            </Link>
            <div className="text-gray-300">|</div>
            <Link 
              to={`/projects/${projectId}`}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              {project?.name || 'Project'}
            </Link>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mt-2">
            📋 Спецификация: {schema.name}
          </h1>
          {schema.description && (
            <p className="text-gray-600 mt-1">{schema.description}</p>
          )}
          
          <div className="mt-3 text-sm text-gray-500">
            Всего элементов: <span className="font-semibold">{totalItems}</span>
            {' '}| Уникальных типов: <span className="font-semibold">{specificationItems.length}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6">
        {Object.keys(groupedByType).length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="text-gray-400 text-6xl mb-4">📦</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Пустая спецификация</h3>
            <p className="text-gray-600">На схеме пока нет размещенных устройств</p>
            <Link 
              to={`/projects/${projectId}/schemas/${schemaId}`}
              className="btn-primary mt-4 inline-block"
            >
              Перейти к редактору схемы
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedByType).map(([deviceType, items]) => (
              <div key={deviceType} className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {getDeviceTypeDisplayName(deviceType)}
                  </h2>
                  <div className="text-sm text-gray-500">
                    {items.length} типов, {items.reduce((sum, item) => sum + item.count, 0)} штук
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Наименование
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Количество
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Позиционные обозначения
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {items.map((item) => (
                        <tr key={item.specification_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {item.spec_name}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {item.count} шт.
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {item.positions.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {item.positions.map((position, index) => (
                                    <span 
                                      key={index}
                                      className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs"
                                    >
                                      {position}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-400 italic">без маркировки</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="bg-white border-t mt-8">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              Последнее обновление: {new Date().toLocaleString('ru-RU')}
            </div>
            <div className="space-x-2">
              <button className="btn-secondary text-sm">
                📄 Экспорт в Excel
              </button>
              <button className="btn-secondary text-sm">
                🖨️ Печать
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 